import { useCallback, useMemo } from 'react';
import { fromJS } from 'immutable';
import styled from 'styled-components';
import { ChannelTypes } from 'conversations-internal-schema/constants/ChannelTypes';
import { HELPDESK } from '../../shared/constants/workspaces';
import { useIsUngatedForHelpDeskRoutingScopeFiltering } from '../../auth/hooks/useGates';
import { isValid } from '../operators/isValid';
import { checkCanHaveFallback, checkHasUsersAndTeams, getDistribution } from '../operators/routingRulesGetters';
import { setDistribution, setUsersAndTeams } from '../operators/routingRulesSetters';
import { getChannelRoutingDistribution } from '../utils/distribution';
import { DISTRIBUTIONS } from '../constants/distributions';
import AssignToAvailableCheckbox from './AssignToAvailableCheckbox';
import { DistributionSelect } from './DistributionSelect';
import FallbackRow from './FallbackRow';
import RoundRobinRow from './RoundRobinRow';
import RoutingRulesSelectRow from './RoutingRulesSelectRow';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RoutingRulesWrapper = styled.div.withConfig({
  displayName: "RoutingRulesFetchTeams__RoutingRulesWrapper",
  componentId: "sc-1w3rgru-0"
})(["width:100%;"]);
const RoutingRulesFetchTeams = ({
  aiChatbotId,
  canSelectDistribution,
  currentUser,
  disabled,
  disableKeepAssigneeOption,
  FormControl,
  hasEditAccess,
  hasRoutingAccess,
  hideAvailableUserCheckbox,
  inboxId,
  isLoadingUsersAndTeams,
  objectTypeId,
  onOpenChange,
  onRuleUpdate: onRuleUpdateProp,
  onUsersAndTeamsMount,
  overrides = {},
  routingRules,
  routingSource,
  showAnyUserOption,
  showKeepAssigneeOption,
  showOwnerOption = true,
  showSelectionRow = true,
  showTeamsOnlyOption,
  showUnassignedOption = true,
  trackUsage = () => {},
  use,
  userAndTeamSelectValueComponent,
  users,
  workSpace
}) => {
  const isUngatedForScopeFiltering = useIsUngatedForHelpDeskRoutingScopeFiltering();
  //In the future, we will be removing hasEditAcccess and hasRoutingAccess in favor of user just providing this as part of the 'disabled' prop.
  const lacksScope = isUngatedForScopeFiltering && workSpace === HELPDESK ? !hasEditAccess : !hasRoutingAccess;
  const isDisabled = disabled || lacksScope;
  const immutableUser = fromJS(currentUser);
  const usersAndTeamsSelectError = !isValid(routingRules);
  const anyAvailableUser = use === ChannelTypes.CHAT || use === ChannelTypes.FACEBOOK_MESSENGER;
  const defaultDistribution = getChannelRoutingDistribution(use);
  const showFallbackRow = checkCanHaveFallback(routingRules);
  const showRoundRobinRow = checkHasUsersAndTeams(routingRules);
  const showDistributionRow = canSelectDistribution && defaultDistribution === DISTRIBUTIONS.RANDOM;
  const setChannelDistribution = useMemo(
  // this reduces calls to `getChannelRoutingDistribution` and also
  // allows for better typing then call `setDistribution(distribution, routingRules)`
  () => setDistribution(defaultDistribution), [defaultDistribution]);
  const onRuleUpdate = useMemo(() => {
    if (showDistributionRow) {
      return onRuleUpdateProp;
    }
    return newRoutingRules => onRuleUpdateProp(setChannelDistribution(newRoutingRules));
  }, [onRuleUpdateProp, setChannelDistribution, showDistributionRow]);
  const onChange = useCallback((newUserIds, newTeamIds) => {
    // we do a non-null assertion because this can only be called by
    // `RoundRobinRow` which only shows when the routing rules have users and teams
    // meaning routingRules is defined
    onRuleUpdate(setUsersAndTeams(routingRules, newUserIds, newTeamIds));
  }, [onRuleUpdate, routingRules]);
  return /*#__PURE__*/_jsxs(RoutingRulesWrapper, {
    children: [showSelectionRow && /*#__PURE__*/_jsx(RoutingRulesSelectRow, {
      aiChatbotId: aiChatbotId !== null && aiChatbotId !== void 0 ? aiChatbotId : undefined,
      anyAvailableUser: anyAvailableUser,
      disabled: isDisabled,
      disableKeepAssigneeOption: disableKeepAssigneeOption,
      FormControl: FormControl,
      overrides: overrides.routingTypeSelect,
      showAnyUserOption: showAnyUserOption,
      showKeepAssigneeOption: showKeepAssigneeOption,
      showTeamsOnlyOption: showTeamsOnlyOption,
      showUnassignedOption: showUnassignedOption,
      showOwnerOption: showOwnerOption,
      routingRules: routingRules,
      onOpenChange: onOpenChange,
      onRuleUpdate: onRuleUpdate,
      workSpace: workSpace,
      isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering
    }), showFallbackRow && /*#__PURE__*/_jsx(FallbackRow, {
      disabled: isDisabled,
      FormControl: FormControl,
      routingRules: routingRules,
      onRuleUpdate: onRuleUpdate,
      isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering
    }), showRoundRobinRow && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(RoundRobinRow, {
        currentUser: immutableUser,
        disabled: isDisabled,
        FormControl: FormControl,
        inboxId: inboxId,
        isLoading: isLoadingUsersAndTeams,
        isUngatedForHelpDeskRoutingScopeFiltering: isUngatedForScopeFiltering,
        objectTypeId: objectTypeId,
        onUserOrTeamSelect: onChange,
        onUsersAndTeamsMount: onUsersAndTeamsMount,
        routingRules: routingRules,
        routingSource: routingSource,
        shouldFetchTeams: true,
        showLabel: false,
        trackUsage: trackUsage,
        userAndTeamSelectValueComponent: userAndTeamSelectValueComponent,
        usersAndTeamsSelectError: usersAndTeamsSelectError,
        usersOptions: users,
        workSpace: workSpace
      }), showDistributionRow && /*#__PURE__*/_jsx(DistributionSelect, {
        channelType: use,
        disabled: isDisabled,
        distribution: getDistribution(routingRules),
        onChange: newDistribution => {
          onRuleUpdateProp(
          // this is ok to do because for this to show we know that
          // `routingRules` is defined
          setDistribution(newDistribution)(routingRules));
        }
      }), !hideAvailableUserCheckbox && /*#__PURE__*/_jsx(AssignToAvailableCheckbox, {
        channelType: use,
        disabled: isDisabled,
        objectTypeId: objectTypeId,
        onRuleUpdate: onRuleUpdate,
        routingRules: routingRules
      })]
    })]
  });
};
RoutingRulesFetchTeams.displayName = 'RoutingRulesFetchTeams';
export default RoutingRulesFetchTeams;